import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import Alert from '@/components/base/Alert';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import AuthLayout from '../components/AuthLayout';
import PasswordField from '../components/PasswordField';

interface LocationState {
  from?: string;
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, isAuthenticated, initializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!initializing && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const validate = () => {
    const next: { email?: string; password?: string } = {};
    const trimmed = email.trim();
    if (!trimmed) next.email = t('common.required');
    if (!password) next.password = t('common.required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setRejectReason(null);
    if (!validate()) return;

    setLoading(true);
    const result = await login(email.trim(), password);
    setLoading(false);

    if (result.ok) {
      navigate(from, { replace: true });
      return;
    }
    if (result.code === 'pending') setFormError(t('auth.login.errorPending'));
    else if (result.code === 'rejected') {
      setFormError(t('auth.login.errorRejected'));
      if (result.reason) setRejectReason(result.reason);
    } else setFormError(t('auth.login.errorInvalid'));
  };

  const onQuickDemo = async () => {
    setFormError(null);
    setRejectReason(null);
    setLoading(true);
    const result = await login('laundry@laundrybox.sa', '12345678');
    setLoading(false);

    if (result.ok) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (result.code === 'pending') setFormError(t('auth.login.errorPending'));
    else if (result.code === 'rejected') {
      setFormError(t('auth.login.errorRejected'));
      if (result.reason) setRejectReason(result.reason);
    } else setFormError(t('auth.login.errorInvalid'));
  };

  return (
    <AuthLayout>
      <div className="lb-glass rounded-lg p-6 animate-fade-in-up md:p-8">
        <h1 className="text-xl font-bold text-foreground-950 md:text-2xl">{t('auth.login.title')}</h1>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{t('auth.login.subtitle')}</p>

        {formError ? (
          <div className="mt-5">
            <Alert variant="error" title={formError}>
              {rejectReason ? (
                <p className="text-sm">
                  <span className="font-medium">{t('auth.login.reasonLabel')}</span> {rejectReason}
                </p>
              ) : null}
            </Alert>
          </div>
        ) : null}

        <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
          <Input
            id="login-email"
            name="email"
            label={t('auth.login.email')}
            placeholder={t('auth.login.emailPlaceholder')}
            type="email"
            ltr
            leadingIcon="ri-mail-line"
            autoComplete="email"
            requiredMark
            value={email}
            error={errors.email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <PasswordField
            id="login-password"
            name="password"
            label={t('auth.login.password')}
            placeholder={t('auth.login.passwordPlaceholder')}
            autoComplete="current-password"
            requiredMark
            value={password}
            error={errors.password}
            showLabel={t('auth.login.showPassword')}
            hideLabel={t('auth.login.hidePassword')}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
            {loading ? t('common.processing') : t('auth.login.submit')}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="lg"
            fullWidth
            icon="ri-flashlight-line"
            disabled={loading}
            onClick={onQuickDemo}
          >
            {t('auth.login.quickDemo')}
          </Button>
        </form>

        <div className="mt-4 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-accent-600 hover:text-accent-700"
          >
            {t('auth.login.forgot')}
          </Link>
          <Link
            to="/register"
            className="text-sm font-medium text-foreground-600 hover:text-foreground-900"
          >
            {t('auth.login.registerLink')}
          </Link>
        </div>

        <div className="mt-6 border-t border-background-200 pt-5">
          <Alert variant="info" title={t('common.demoData')}>
            <ul className="space-y-1 text-xs">
              <li className="lb-ltr-field text-start">{t('auth.login.demoHint')}</li>
              <li className="lb-ltr-field text-start">{t('auth.login.demoPendingHint')}</li>
              <li className="lb-ltr-field text-start">{t('auth.login.demoRejectedHint')}</li>
            </ul>
          </Alert>
        </div>
      </div>
    </AuthLayout>
  );
}